import L from 'leaflet';
import type { LatLngExpression } from 'leaflet';
import type { LeafletMouseEvent } from 'leaflet';
import type { FitBoundsOptions } from 'leaflet';

import type { AreaMapShape } from '@/lib/areas';

/** True when the event target is an overlay/marker/control (not bare map tiles). */
export function isLeafletOverlayOrControlClick(e: LeafletMouseEvent): boolean {
  const t = e.originalEvent?.target as HTMLElement | null | undefined;
  return !!(t && typeof t.closest === 'function' && t.closest('.leaflet-interactive, .leaflet-marker-icon, .leaflet-control'));
}

/** Ground meters per degree latitude (approximation). */
export const METERS_PER_DEG_LAT = 111_320;

/** Oval segment count for editor / precise overlays (LocationPicker). */
export const OVAL_POLYGON_SEGMENTS_EDITOR = 128;

/** Lighter oval mesh for read-only map views (LocationControl). */
export const OVAL_POLYGON_SEGMENTS_DISPLAY = 64;

export function metersPerDegLng(atLat: number): number {
  return METERS_PER_DEG_LAT * Math.cos((atLat * Math.PI) / 180);
}

/** U and V unit axes (degrees clockwise from north); wx runs along U, wy along V. */
export function axisBasisDegrees(orientationDeg: number) {
  const t = (orientationDeg * Math.PI) / 180;
  return {
    ue: Math.sin(t),
    un: Math.cos(t),
    ve: Math.cos(t),
    vn: -Math.sin(t),
  };
}

export function rectangleLatLngs(
  lat: number,
  lng: number,
  widthM: number,
  heightM: number,
  orientationDeg: number,
): LatLngExpression[] {
  const { ue, un, ve, vn } = axisBasisDegrees(orientationDeg);
  const mLat = METERS_PER_DEG_LAT;
  const mLng = metersPerDegLng(lat);
  const toLatLng = (de: number, dn: number): [number, number] => [lat + dn / mLat, lng + de / mLng];
  const corners = [
    { wx: -widthM / 2, wy: -heightM / 2 },
    { wx: widthM / 2, wy: -heightM / 2 },
    { wx: widthM / 2, wy: heightM / 2 },
    { wx: -widthM / 2, wy: heightM / 2 },
  ];
  return corners.map(({ wx, wy }) => {
    const de = wx * ue + wy * ve;
    const dn = wx * un + wy * vn;
    return toLatLng(de, dn);
  });
}

/**
 * Rectangle for a stored placement: `heightM` spans local U, `widthM` spans V.
 * Matches placement semantics used by the API and LocationPicker handles.
 */
export function placementRectangleLatLngs(
  lat: number,
  lng: number,
  widthM: number,
  heightM: number,
  orientationDeg: number,
): LatLngExpression[] {
  return rectangleLatLngs(lat, lng, heightM, widthM, orientationDeg);
}

/** Ellipse matching placement frame: `heightM` along U, `widthM` along V. */
export function placementOvalLatLngs(
  lat: number,
  lng: number,
  widthM: number,
  heightM: number,
  orientationDeg: number,
  segments: number = OVAL_POLYGON_SEGMENTS_EDITOR,
): LatLngExpression[] {
  const { ue, un, ve, vn } = axisBasisDegrees(orientationDeg);
  const mLat = METERS_PER_DEG_LAT;
  const mLng = metersPerDegLng(lat);
  const toLatLng = (de: number, dn: number): [number, number] => [lat + dn / mLat, lng + de / mLng];
  const halfU = heightM / 2;
  const halfV = widthM / 2;
  const pts: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const t = (2 * Math.PI * i) / segments;
    const wx = halfU * Math.cos(t);
    const wy = halfV * Math.sin(t);
    const de = wx * ue + wy * ve;
    const dn = wx * un + wy * vn;
    pts.push(toLatLng(de, dn));
  }
  return pts;
}

/** Minimal fields for “has the area been fully placed on the map?”. */
export type MapPlacementLike = {
  latitude?: number | null;
  longitude?: number | null;
  shape?: AreaMapShape | null;
  widthM?: number | null;
  heightM?: number | null;
  orientationDegrees?: number | null;
};

export function hasFullMapPlacement(a: MapPlacementLike): boolean {
  return (
    a.latitude != null &&
    a.longitude != null &&
    a.shape != null &&
    a.widthM != null &&
    a.heightM != null &&
    a.orientationDegrees != null &&
    !Number.isNaN(Number(a.latitude)) &&
    !Number.isNaN(Number(a.longitude))
  );
}

/** Committed placement with numeric map fields (after `hasFullMapPlacement`). */
export type PlacementPolygonInput = {
  latitude: number;
  longitude: number;
  shape: AreaMapShape;
  widthM: number;
  heightM: number;
  orientationDegrees: number;
};

export function placementPolygonLatLngs(
  p: PlacementPolygonInput,
  ovalSegments: number = OVAL_POLYGON_SEGMENTS_EDITOR,
): LatLngExpression[] {
  return p.shape === 'oval'
    ? placementOvalLatLngs(p.latitude, p.longitude, p.widthM, p.heightM, p.orientationDegrees, ovalSegments)
    : placementRectangleLatLngs(p.latitude, p.longitude, p.widthM, p.heightM, p.orientationDegrees);
}

function extendBoundsWithPoints(b: L.LatLngBounds | null, pts: LatLngExpression[]): L.LatLngBounds | null {
  let out = b;
  for (const pt of pts) {
    const ll = L.latLng(pt as [number, number]);
    out = out ? out.extend(ll) : L.latLngBounds(ll, ll);
  }
  return out;
}

/** Union bounds for areas that pass `hasFullMapPlacement`; `null` if none. */
export function boundsFromMapPlacementsNullable(
  list: readonly MapPlacementLike[],
  ovalSegments: number = OVAL_POLYGON_SEGMENTS_DISPLAY,
): L.LatLngBounds | null {
  let b: L.LatLngBounds | null = null;
  for (const a of list) {
    if (!hasFullMapPlacement(a)) continue;
    const pts = placementPolygonLatLngs(
      {
        latitude: a.latitude!,
        longitude: a.longitude!,
        shape: a.shape!,
        widthM: a.widthM!,
        heightM: a.heightM!,
        orientationDegrees: a.orientationDegrees!,
      },
      ovalSegments,
    );
    b = extendBoundsWithPoints(b, pts);
  }
  return b;
}

/**
 * Union bounds for committed placements (LocationPicker).
 * @throws if `placed` is empty
 */
export function boundsFromMapPlacementsStrict(
  placed: readonly PlacementPolygonInput[],
  ovalSegments: number = OVAL_POLYGON_SEGMENTS_EDITOR,
): L.LatLngBounds {
  let b: L.LatLngBounds | null = null;
  for (const p of placed) {
    const pts = placementPolygonLatLngs(p, ovalSegments);
    b = extendBoundsWithPoints(b, pts);
  }
  if (!b) {
    throw new Error('boundsFromMapPlacementsStrict: empty placed array');
  }
  return b;
}

/** Square bounds centered at (lat0,lng0) with side length `extentMeters` (ground meters). */
export function squareBoundsMeters(lat0: number, lng0: number, extentMeters: number): L.LatLngBounds {
  const half = extentMeters / 2;
  const mLng = metersPerDegLng(lat0);
  const dLat = half / METERS_PER_DEG_LAT;
  const dLng = half / mLng;
  return L.latLngBounds([lat0 - dLat, lng0 - dLng], [lat0 + dLat, lng0 + dLng]);
}

export type SiteLatLngLike = {
  latitude?: number | null;
  longitude?: number | null;
};

export function boundsFromSitesNullable(sites: readonly SiteLatLngLike[]): L.LatLngBounds | null {
  let b: L.LatLngBounds | null = null;
  for (const s of sites) {
    if (s.latitude == null || s.longitude == null) continue;
    if (Number.isNaN(Number(s.latitude)) || Number.isNaN(Number(s.longitude))) continue;
    const ll = L.latLng(Number(s.latitude), Number(s.longitude));
    b = b ? b.extend(ll) : L.latLngBounds(ll, ll);
  }
  return b;
}

export function boundsFromSitesAndAreasNullable(
  sites: readonly SiteLatLngLike[],
  areas: readonly MapPlacementLike[],
  ovalSegments: number = OVAL_POLYGON_SEGMENTS_DISPLAY,
): L.LatLngBounds | null {
  let b = boundsFromMapPlacementsNullable(areas, ovalSegments);
  const sb = boundsFromSitesNullable(sites);
  if (sb && sb.isValid()) {
    b = b ? b.extend(sb) : sb;
  }
  return b;
}

/**
 * Same end view as `map.fitBounds`, but calls `setView` with only `{ animate: false }`.
 * Leaflet's `fitBounds` forwards padding/maxZoom into `setView`, which is noisier than needed
 * for programmatic fits while react-leaflet updates layers.
 */
export function applyMapFitBounds(map: L.Map, bounds: L.LatLngBounds, fitOptions: FitBoundsOptions) {
  const priv = map as L.Map & {
    _getBoundsCenterZoom(b: L.LatLngBounds, o?: FitBoundsOptions): { center: L.LatLng; zoom: number };
  };
  const target = priv._getBoundsCenterZoom(bounds, fitOptions);
  let z = target.zoom;
  if (typeof fitOptions.maxZoom === 'number') {
    z = Math.min(fitOptions.maxZoom, z);
  }
  if (z === Infinity || !Number.isFinite(z)) {
    z = map.getBoundsZoom(bounds, false, L.point(56, 56));
  }
  z = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), z));
  map.setView(target.center, z, { animate: false });
}
